import { handleTrackClick } from "~/lib/analytics/handleTrackClick";
import { getBestArticleId } from "~/lib/getBestArticleId";
import PodcastPlayer from "~features/media/PodcastPlayer";
import { cx, formatAuthors, formatDate } from "~utils";
import { Image } from "./ArticleImage";
import { Kicker } from "~ui/Kicker";

import styles from "./article-list.module.css";

function ArticleTitle({ articleTitleTagType = "h2", text, underlineLink }) {
  const Tag = articleTitleTagType;
  return (
    <Tag
      className={cx(styles.articleTitle, underlineLink ? styles.underlineLink : null)}
      dangerouslySetInnerHTML={{ __html: text }}
    ></Tag>
  );
}

/**
 * Main article-preview component.
 *
 * @param {Object} props
 * @param {className} props.string
 * @param {Object} props.article
 * @param {('grid'|'hero')} [props.layout]
 * @param {boolean} [props.isDarkMode]
 * @param {boolean} [props.showCategory]
 * @param {boolean} [props.showDek]
 * @param {boolean} [props.showImage]
 * @param {boolean} [props.showMedia]
 *
 * @returns {React.JSX.Element}
 */
const ArticleDisplay = ({
  className,
  article,
  articleTitleTagType = "h2",
  layout = "grid",
  clickTrackKey = "",
  clickTrackIndex = "",
  lazyload = true,
  isDarkMode = false,
  showCategory = true,
  showDek = true,
  showImage = true,
  showMedia = false,
  inAllSponsorContentList = false,
  underlineLink = false,
}) => {
  let {
    advertiser = "",
    authors,
    display_title,
    display_date,
    media_url,
    release_date,
    date_published,
    summary,
    subtype,
    url,
    category,
    image_url,
    image_alt_text = "",
    image_block_syndication,
    digital_column,
  } = article;

  // Is this a Custom Media article?
  const isSponsorContent = !!advertiser;

  // For sponsor content, if the list is ALL sponsor content,
  // the item should look like regular articles and show authors.
  //
  // But if it's going to appear within an Editorial list
  // - show "Sponsored" instead of the category
  // - use the advertiser name as the author name
  // - style with the gray background
  const injectedNativePlacement = isSponsorContent && !inAllSponsorContentList;
  if (injectedNativePlacement) {
    category = "Sponsored";
    authors = advertiser;
  }

  // sponsored content (aka custom media) has a String of authors, not an Array of them, so only run this function if it's an Array
  const authorsNameOrNames = Array.isArray(authors) ? formatAuthors(authors) : authors;
  const articleId = getBestArticleId(article);

  // fallback img for podcasts w/o one
  if (!image_url && subtype === "Podcast") {
    // @TODO: use an import for this
    image_url = "https://www.scientificamerican.com/static/science-quickly-logo.jpg";
  } else if (!image_url) {
    image_url = "https://www.scientificamerican.com/static/sciam-mark.jpg";
  }

  return (
    <article
      className={cx(
        styles.article,
        isDarkMode && styles.isDarkMode,
        injectedNativePlacement && styles.native,
        className,
      )}
    >
      <a
        className={styles.articleLink}
        href={url}
        data-testid="article-link"
        onClick={(e) => {
          let clickTrackName = "articlelist-";
          let clickTrackPos = "";
          // Default click track name is set here
          let clickTrackNameFull = clickTrackName + articleId;

          // If clickTrackKey is passed, override the track name
          if (clickTrackKey) {
            clickTrackPos = clickTrackIndex;
            clickTrackNameFull = clickTrackKey + clickTrackPos + "-" + articleId;
          }

          handleTrackClick(clickTrackNameFull, e);
        }}
      >
        {showImage && image_url ? (
          <Image
            category={category}
            image={image_url}
            lazyload={lazyload}
            layout={layout}
            imageAltText={image_alt_text}
            imageBlockSyndication={image_block_syndication}
          />
        ) : null}
        <div className={styles.kickerContainer}>
          <Kicker
            category={category}
            display_date={display_date}
            isDarkMode={isDarkMode}
            release_date={date_published || release_date}
            showCategory={showCategory}
            subtype={subtype}
            digital_column={digital_column}
          />
        </div>
        <ArticleTitle
          articleTitleTagType={articleTitleTagType}
          text={display_title}
          underlineLink={underlineLink}
        />
      </a>
      <section className={styles.articleInfo}>
        {showDek ? (
          <div className={cx(styles.dek)} dangerouslySetInnerHTML={{ __html: summary }} />
        ) : null}
        <p className={styles.authors} dangerouslySetInnerHTML={{ __html: authorsNameOrNames }} />
      </section>
      {showMedia && media_url ? (
        <div className={styles.articleFooter}>
          {/* TODO: This component is heavy, refactor ArticleDisplay to use a composition pattern */}
          <PodcastPlayer mediaUrl={media_url} title={display_title} />
        </div>
      ) : null}
    </article>
  );
};

export default ArticleDisplay;
